'use client';

import { useRef, useState } from 'react';
import { Text } from '@lmn/design-system';
import { IconArrowDownSolid } from '@/app/common/icons/libraryIcons';
import styled, { css } from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useIsClamped } from '@/app/common/hooks/useIsClamped';
import { getLanguageFromLocale } from '@/app/components/search/utils/misc';
import { TA_CATEGORY_TEXT_OFFSET_PX, TA_TRIP_TYPE_TRANSLATION_KEYS } from './tripadvisor.helper';
import TripAdvisorBubbles from './TripAdvisorBubbles';

const Card = styled.div<{ $inline?: boolean; $alignWithCategories?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 0;
  border-top: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
  /* TripAdvisor content is user generated and often arrives as a single unbroken token:
     usernames such as benedikte_myrmellom, or titles and bodies written without spaces.
     'anywhere' rather than 'break-word' so the token also stops inflating the flex min-content
     size — otherwise the user column grows past its 200px and the name spills over the review
     body (MPH-1011). */
  overflow-wrap: anywhere;

  @media ${customCss.breakpoints.sm} {
    flex-direction: row;
    gap: ${({ $inline }) => ($inline ? '32px' : '24px')};
  }

  /* MPH-1205: on desktop in the Evo details modal the card joins the reviews content's two-column
     grid (see TripAdvisorReviewsModalContent): the user column sits under the score block and the
     content column under the categories, padded by the same offset as the category names and bars,
     so the bubbles, title and text start on the bars' left edge. Without subgrid support the card
     keeps the flex row above. */
  ${({ $alignWithCategories }) =>
    $alignWithCategories &&
    css`
      @supports (grid-template-columns: subgrid) {
        @media ${customCss.breakpoints.lg} {
          display: grid;
          grid-template-columns: subgrid;
          grid-column: 1 / -1;
          gap: 0;

          ${UserCol} {
            grid-column: 1;
          }

          ${Content} {
            grid-column: 2;
            padding-left: ${TA_CATEGORY_TEXT_OFFSET_PX}px;
          }
        }
      }
    `}
`;

const UserCol = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media ${customCss.breakpoints.sm} {
    width: 200px;
    align-items: flex-start;
  }
`;

// Evo (inline) follows the Figma DS avatar: 48px, regular-weight
// initial. The legacy modal keeps the smaller bold avatar.
const Avatar = styled.div<{ $inline?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $inline }) => ($inline ? '48px' : '40px')};
  height: ${({ $inline }) => ($inline ? '48px' : '40px')};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  font-weight: ${({ $inline }) => ($inline ? 400 : 700)};
  ${({ theme, $inline }) => $inline && theme.kallisto.mixins.font('Body')};
  text-transform: uppercase;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Content = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $inline }) => ($inline ? '8px' : '6px')};
  flex: 1;
  min-width: 0;
`;

const BubblesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* A plain <p> rather than the DS Text so the clamped box itself can be measured
   (see useIsClamped) — same tag, typography and colour Text gives variant="Body". */
const Body = styled.p<{ $expanded: boolean }>`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('Body')};

  ${({ $expanded }) =>
    $expanded
      ? ''
      : `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const ReadMore = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('Body')};

  span {
    text-decoration: underline;
  }
`;

const ReadMoreChevron = styled(IconArrowDownSolid)<{ $expanded: boolean }>`
  transition: transform 0.2s ease;
  ${({ $expanded }) => $expanded && 'transform: rotate(180deg);'}
`;

type ReviewTextEntry = { language: string; value: string; primary: boolean };

function getPrimaryEntry(items: ReviewTextEntry[], locale: string): ReviewTextEntry | undefined {
  return (
    items.find((t) => t.primary && t.value) ??
    items.find((t) => t.language === getLanguageFromLocale(locale) && t.value) ??
    items.find((t) => t.value)
  );
}

/**
 * MPH-1202: a review can be in another language than the page, so its title and text carry their own
 * `lang` for screen readers to switch voice (WCAG 2.2 SC 3.1.2 Language of Parts). TA codes such as
 * `zh_TW` become BCP 47 `zh-TW`.
 */
const toHtmlLang = (entry?: ReviewTextEntry): string | undefined => entry?.language?.replace('_', '-') || undefined;

function formatReviewDate(review: TripadvisorReview, locale: string): string {
  const raw = review.publish_ts || review.travel_date;
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

type TripAdvisorReviewCardProps = {
  review: TripadvisorReview;
  locale: string;
  /** Evo details modal reviews tab: applies the Figma DS card styling (48px avatar, textPrimary meta). */
  inline?: boolean;
  /**
   * The card sits in the reviews content's shared desktop grid and starts its review content on the
   * summary's category bars (MPH-1205). Only set together with that grid, see
   * TripAdvisorReviewsModalContent.
   */
  alignWithCategories?: boolean;
};

export default function TripAdvisorReviewCard({
  review,
  locale,
  inline = false,
  alignWithCategories = false,
}: TripAdvisorReviewCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  // Only offer the toggle when the clamp is really hiding something: a short review that
  // happens to be long in characters still fits in three lines on a wide card.
  const isClamped = useIsClamped(bodyRef, !expanded);
  const titleEntry = getPrimaryEntry(review.title, locale);
  const bodyEntry = getPrimaryEntry(review.text, locale);
  const title = titleEntry?.value ?? '';
  const body = bodyEntry?.value ?? '';
  const date = formatReviewDate(review, locale);
  const initial = review.user?.username?.trim().charAt(0) || '?';
  const tripTypeKey = TA_TRIP_TYPE_TRANSLATION_KEYS[review.trip_type];
  const tripTypeLabel = tripTypeKey && review.trip_type !== 'NONE' ? t(tripTypeKey) : '';

  // Evo/Figma renders the traveller meta in textPrimary; the legacy modal keeps it secondary.
  const metaColor = inline ? 'textPrimary' : 'textSecondary';

  return (
    <Card $inline={inline} $alignWithCategories={alignWithCategories}>
      <UserCol>
        <Avatar $inline={inline}>{initial}</Avatar>
        <UserInfo>
          <Text variant="Body" weight="bold" color="textPrimary">
            {review.user?.username}
          </Text>
          {tripTypeLabel ? (
            <Text variant="BodySmall" color={metaColor}>
              {t('vacation.select.details.guest.reviews.filter.travelers.title')}: {tripTypeLabel}
            </Text>
          ) : (
            // TA display requirement: fall back to traveler attribution when no trip type is provided
            <Text variant="BodySmall" color={metaColor}>
              {t('vacation.select.details.guest.reviews.tripadvisor.single_attribution') ||
                'A Tripadvisor traveler review'}
            </Text>
          )}
        </UserInfo>
      </UserCol>

      <Content $inline={inline}>
        <BubblesRow>
          <TripAdvisorBubbles value={review.rating} css="font-size: 14px;" />
          {date && (
            <Text variant="BodySmall" color="textSecondary">
              {date}
            </Text>
          )}
        </BubblesRow>

        {title && (
          <Text variant="Title4" color="textPrimary" lang={toHtmlLang(titleEntry)}>
            {title}
          </Text>
        )}
        {/* TA display requirements: quoted review content must appear within quotation marks */}
        {body && (
          <Body ref={bodyRef} $expanded={expanded} lang={toHtmlLang(bodyEntry)}>
            “{body}”
          </Body>
        )}
        {body && (isClamped || expanded) && (
          <ReadMore type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
            <span>
              {expanded ? t('vacation.select.facilities.showless') : t('vacation.select.facilities.showmore')}
            </span>
            <ReadMoreChevron size={16} $expanded={expanded} />
          </ReadMore>
        )}
      </Content>
    </Card>
  );
}
